import { type VariantProps, cva } from 'class-variance-authority';

export { default as Button } from './Button.vue';

// Neutral at rest, accent reserved for hover. In a monitoring product orange is
// semantically loaded, so a table where a third of the cells are orange reads as
// alarms when it is only navigation. Exported so non-Button links (render
// functions, plain anchors) style themselves from the same source.
//
// Weight carries "this is a link" at rest rather than an underline: body cells
// render at the normal weight in the same colour, so 500 against 400 separates
// them without ruling a line under every row in a dense table. The underline is
// held back for hover, where it confirms the target alongside the accent.
export const linkClasses =
  'font-medium text-foreground underline-offset-4 transition-colors hover:text-primary hover:underline';

// Modern shadcn/ui button variants (from develop branch)
export const buttonVariants = cva(
  'cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'border-none bg-primary text-primary-foreground hover:bg-primary/80 active:bg-primary/70 disabled:bg-muted disabled:text-muted-foreground disabled:!opacity-100',
        secondary:
          'border border-border bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-hover/80',
        destructive:
          'bg-destructive-surface text-destructive-foreground hover:bg-destructive-surface/80 active:bg-destructive-surface/70',
        outline:
          'border border-border bg-transparent hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: linkClasses,
      },
      size: {
        default: 'px-4 py-2',
        xs: 'rounded px-2 py-1',
        sm: 'rounded-md px-3 py-1.5',
        lg: 'rounded-md px-8',
        icon: 'h-10 w-10',
        // The other sizes derive their height from padding, so a button placed
        // beside an input or select lands a few pixels off. These pin the height
        // instead, for buttons that share a row with other controls.
        control: 'h-9 rounded-md px-3',
        controlIcon: 'h-9 w-9 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;
